/**
 * BarcaChatWidget - Main widget component for managing AI voice conversations
 * Handles widget state, floating button, and conversation flow
 */

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { MessageCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useConversation } from "@elevenlabs/react";
import { BarcaChatConversation } from "./BarcaChatConversation";
import { useBarcaChatConfig } from "../providers/BarcaChatProvider";
import { ChatMode } from "../types";
import { useWidgetManager } from "../hooks/useWidgetManager";

export const BarcaChatWidget = () => {
    const { config } = useBarcaChatConfig();
    const widgetManager = config.useWidgetManager ? useWidgetManager() : null;

    // Use external control if provided, otherwise use internal state
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isOpen = config.externalControl?.isOpen !== undefined
        ? config.externalControl.isOpen
        : (widgetManager ? widgetManager.isActive('voice') : internalIsOpen);

    const [chatMode, setChatMode] = useState<ChatMode>('selection');

    // Add a state to track if the component is mounted on the client
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);

    // ElevenLabs conversation hook for detecting connection status
    const conversation = useConversation();

    const [debouncedIsListening, setDebouncedIsListening] = useState(false);
    const [debouncedIsResponding, setDebouncedIsResponding] = useState(false);

    // Detect listening/responding states for status indicator
    useEffect(() => {
        const isListening = conversation.status === 'connected' && !conversation.isSpeaking;
        const isResponding = conversation.status === 'connected' && conversation.isSpeaking;

        if (isListening) {
            setDebouncedIsListening(true);
            setDebouncedIsResponding(false);
        } else if (isResponding) {
            setDebouncedIsListening(false);
            setDebouncedIsResponding(true);
        } else {
            setDebouncedIsListening(false);
            setDebouncedIsResponding(false);
        }
    }, [conversation.status, conversation.isSpeaking]);

    const toggleWidget = async () => {
        if (config.externalControl) {
            // Use external control callbacks
            if (isOpen) {
                config.externalControl.onCloseRequest?.();
            } else {
                config.externalControl.onOpenRequest?.();
            }
            return;
        }

        if (widgetManager) {
            // Use widget manager
            if (isOpen) {
                widgetManager.requestClose('voice');
                setChatMode('selection');
                config.onWidgetClose?.();
            } else {
                const opened = await widgetManager.requestOpen('voice');
                if (opened) {
                    config.onWidgetOpen?.();
                }
            }
        } else {
            // Use internal state management
            const newIsOpen = !internalIsOpen;
            setInternalIsOpen(newIsOpen);
            if (!newIsOpen) {
                setChatMode('selection');
                config.onWidgetClose?.();
            } else {
                config.onWidgetOpen?.();
            }
        }
    };

    // Update internal state when using widget manager
    useEffect(() => {
        if (widgetManager && widgetManager.isActive('voice')) {
            setInternalIsOpen(true);
        } else if (widgetManager && !widgetManager.isActive('voice')) {
            setInternalIsOpen(false);
            setChatMode('selection');
        }
    }, [widgetManager?.state]);

    const positionClasses = config.position === 'bottom-left'
        ? 'bw-bottom-6 bw-left-6'
        : 'bw-bottom-6 bw-right-6';

    // Determine if floating button should be shown
    const showFloatingButton = !config.hideFloatingButton &&
        (!widgetManager || widgetManager.state.isFloatingButtonVisible);

    // Use the configured z-index or a high default
    const zIndex = config.zIndex ?? 1000;

    // Render nothing on the server
    if (!isMounted) {
        return null;
    }

    return (
        <div className="barca-chat-widget">
            {/* Floating Chat Widget Button */}
            {showFloatingButton && createPortal(
                <div
                    className={cn("barca-chat-widget", "bw-fixed", positionClasses)}
                    style={{ zIndex: zIndex }}
                >
                    <button
                        onClick={toggleWidget}
                        className={cn(
                            "bw-relative bw-w-14 bw-h-14 bw-rounded-full bw-transition-all bw-duration-300",
                            "modern-button bw-flex bw-items-center bw-justify-center",
                            "hover:bw-scale-110 focus:bw-outline-none"
                        )}
                        style={{
                            background: 'linear-gradient(135deg, hsl(214, 100%, 35%) 0%, hsl(214, 100%, 40%) 30%, hsl(344, 95%, 38%) 100%)' // Same balanced gradient for both states
                        }}
                    >
                        {(widgetManager ? widgetManager.isAnyActive() : isOpen) ? (
                            <X className="bw-w-6 bw-h-6 bw-text-white" />
                        ) : (
                            <MessageCircle className="bw-w-6 bw-h-6 bw-text-white" />
                        )}

                        {/* Status indicator dot */}
                        {conversation.status === 'connected' && (
                            <div className={cn(
                                "bw-absolute bw--top-1 bw--right-1 bw-w-3 bw-h-3 bw-rounded-full bw-transition-colors bw-duration-300",
                                debouncedIsListening ? "bw-bg-blue-400" :
                                    debouncedIsResponding ? "bw-bg-orange-400" : "bw-bg-green-400"
                            )} />
                        )}
                    </button>
                </div>,
                document.body
            )}

            {/* Chat Widget */}
            {isOpen && createPortal(
                 <div className="barca-chat-widget">
                    <BarcaChatConversation
                        isOpen={isOpen}
                        setIsOpen={(value: boolean) => {
                            if (!value) {
                                // Handle closing
                                if (config.externalControl) {
                                    config.externalControl.onCloseRequest?.();
                                } else if (widgetManager) {
                                    widgetManager.requestClose('voice');
                                } else {
                                    setInternalIsOpen(false);
                                }
                            } else {
                                // Handle opening (though this is less common from inside the conversation)
                                if (config.externalControl) {
                                    config.externalControl.onOpenRequest?.();
                                } else if (widgetManager) {
                                    widgetManager.requestOpen('voice');
                                } else {
                                    setInternalIsOpen(true);
                                }
                            }
                        }}
                        chatMode={chatMode}
                        setChatMode={setChatMode}
                        zIndex={zIndex + 1} // Ensure the conversation is on top of the button
                    />
                </div>,
                document.body
            )}
        </div>
    );
};
